import { useState, useRef, useEffect, useMemo } from "react";
import { useMathJax } from "../../hooks/useMathJax";
import { useLearnCtx } from "./LearnPage";
import { useDispatch, useSelector } from "react-redux";
import {
  SIGNAL_START_STOPWATCH,
  ADD_NEW_COMMENT,
} from "../../constants/actionConstants";
import { COMMENT_UNKNOWN } from "../../constants/commentsConstants";
import { retrieve_nearby_text_content } from "../Global/CommentUtils";
import ObjectID from "bson-objectid";
import { addComment } from "../../actions/comment";
import { useParams } from "react-router-dom";

const STOP_WORDS = new Set([
  "a",
  "an",
  "the",
  "and",
  "or",
  "but",
  "in",
  "on",
  "at",
  "to",
  "for",
  "of",
  "with",
  "by",
  "is",
  "are",
  "was",
  "were",
  "be",
  "been",
  "being",
  "have",
  "has",
  "had",
  "do",
  "does",
  "did",
  "will",
  "would",
  "could",
  "should",
  "may",
  "might",
  "can",
  "it",
  "its",
  "this",
  "that",
  "these",
  "those",
  "i",
  "you",
  "he",
  "she",
  "we",
  "they",
  "what",
  "how",
  "why",
  "when",
  "where",
  "which",
  "who",
  "not",
  "no",
  "so",
  "if",
  "as",
  "from",
  "up",
  "about",
]);

function tokenize(str) {
  return str
    .toLowerCase()
    .split(/\W+/)
    .filter((t) => t.length > 1 && !STOP_WORDS.has(t));
}

function HighlightPreview({ text }) {
  const ref = useRef(null);
  const processed = (text ?? "")
    .replace(/\\begin\{align\}/g, "\\begin{align*}")
    .replace(/\\end\{align\}/g, "\\end{align*}")
    .replace(/\\tag\*?\{[^{}]*\}/g, "")
    .trim();
  useMathJax(ref, [processed]);
  return (
    <p
      ref={ref}
      className="qd__highlight-text"
      dangerouslySetInnerHTML={{ __html: processed }}
    />
  );
}

function SendIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 13 13"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M1.5 6.5h9M7 2.5l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Question/comment dialog attached to a frame.
 * Shows existing threads when idle; switches to autocomplete (questions + support)
 * while the user types.
 * @param {{ frameId: string, onClose: () => void }} props
 */
export default function QuestionDialog({
  frameId,
  sectionId,
  sectionIndex,
  onClose,
  highlightedInfo,
}) {
  const { commentsMap, openComments, openSupport } = useLearnCtx();
  const [text, setText] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [showHighlightPanel, setShowHighlightPanel] =
    useState(!!highlightedInfo);
  const textareaRef = useRef(null);
  const listRef = useRef(null);
  const dispatch = useDispatch();
  const { id } = useParams();
  const courseTitle = useSelector(
    (state) => state.CourseStructureReducer.courseTitle,
  );
  const content = useSelector((state) => state.ContentReducer.content);

  const existing = (commentsMap[frameId] ?? []).filter((c) => !c.deleted);
  const query = text.trim();

  // Retrieve content from reducer
  const rawSupport = useSelector((state) => state.SupportReducer.supportItems);
  /* Built once — only records with keywords are searchable support articles */
  const supportItems = rawSupport
    .filter((r) => Array.isArray(r.keywords))
    .map((r) => ({ id: r._id, label: r.title }));

  /* Auto-focus on mount */
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  /* When a new highlighted context arrives (dialog already open), show the panel and re-focus */
  useEffect(() => {
    if (highlightedInfo) {
      setShowHighlightPanel(true);
      textareaRef.current?.focus();
    }
  }, [highlightedInfo]);

  /* Auto-grow textarea; expand to 3 lines once the user clicks or types */
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    const computed = window.getComputedStyle(ta);
    const lineH =
      parseFloat(computed.lineHeight) || parseFloat(computed.fontSize) * 1.5;
    const minH = expanded ? lineH * 3 : lineH;
    ta.style.height = "auto";
    ta.style.height = `${Math.max(ta.scrollHeight, minH)}px`;
  }, [text, expanded]);

  /* Autocomplete: existing questions matching typed tokens */
  const questionMatches = useMemo(() => {
    if (!query) return [];
    const tokens = tokenize(query);
    if (tokens.length === 0) return existing;
    return existing.filter((c) =>
      tokens.some((t) => (c.title ?? "").toLowerCase().includes(t)),
    );
  }, [query, existing]);

  /* Autocomplete: support topics matching query substring */
  const supportMatches = useMemo(() => {
    if (!query) return [];
    const q = query.toLowerCase();
    return supportItems
      .filter((s) => s.label.toLowerCase().includes(q))
      .slice(0, 8);
  }, [query]);

  /* Flat list for keyboard index tracking */
  const allSuggestions = useMemo(
    () => [
      ...questionMatches.map((c) => ({
        id: c._id,
        label: c.title,
        kind: "question",
        commentType: c.type,
      })),
      ...supportMatches.map((s) => ({
        id: s.id,
        label: s.label,
        kind: "support",
      })),
    ],
    [questionMatches, supportMatches],
  );

  const handleSelect = (item) => {
    if (item.kind === "question") {
      openComments(frameId, item.id);
      if (!window.matchMedia("(min-width: 2225px)").matches) onClose();
    } else {
      openSupport(item.id);
      onClose();
    }
  };

  const handleSubmit = (e) => {
    e.stopPropagation();

    // Signal that we have interacted
    dispatch({ type: SIGNAL_START_STOPWATCH, payload: Math.random() });

    var date_object = new Date();
    const user = JSON.parse(localStorage.getItem("profile"));

    // If comment short, then title is what it is.  If comment not short then truncate for title
    const title_text = text.length < 50 ? text : text.substring(0, 47) + "...";

    // Create placeholder reply for AI
    let reply = {
      author: "AI",
      author_id: user.result._id,
      date: date_object.getTime(),
      _id: ObjectID().toHexString(),
      upvotes: 0,
      contents: "AI response pending",
      children: [],
    };

    // Create placeholder comment for now
    let newComment = {
      author: user.result.name,
      author_id: user.result._id,
      date: date_object.getTime(),
      _id: ObjectID().toHexString(),
      upvotes: 0,
      type: COMMENT_UNKNOWN,
      title: title_text,
      contents: text,
      children: [reply],
      content_item_id: frameId,
      contentId: id,
      deleted: false,
      highlightedInfo: showHighlightPanel ? (highlightedInfo ?? "") : "",
      courseTitle: courseTitle,
    };

    // This is received internally by reducer (will happen quickly)
    dispatch({ type: ADD_NEW_COMMENT, payload: { sectionIndex, newComment } });

    // Open the new comment thread (also closes support panel and, at narrow widths, this dialog)
    openComments(frameId, newComment._id);

    // Empty the submission box and close this dialog
    setText("");
    onClose();

    // Submit to the database, which will edit with AI
    const frame_text = retrieve_nearby_text_content(content, frameId);
    dispatch(addComment(id, sectionIndex, sectionId, newComment, frame_text));
  };

  const handleTextareaKeyDown = (e) => {
    if (e.key === "Tab" && !e.shiftKey && query && allSuggestions.length > 0) {
      e.preventDefault();
      listRef.current?.querySelector('[data-idx="0"]')?.focus();
    } else if (e.key === "ArrowDown" && query && allSuggestions.length > 0) {
      e.preventDefault();
      listRef.current?.querySelector('[data-idx="0"]')?.focus();
    }
  };

  const handleItemKeyDown = (e, idx) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      listRef.current?.querySelector(`[data-idx="${idx + 1}"]`)?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (idx === 0) textareaRef.current?.focus();
      else listRef.current?.querySelector(`[data-idx="${idx - 1}"]`)?.focus();
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleSelect(allSuggestions[idx]);
    } else if (e.key === "Escape") {
      textareaRef.current?.focus();
    }
  };

  return (
    <div className="qd">
      <div className="qd__header">
        <span className="qd__title">Discussion</span>
        <button className="qd__close" onClick={onClose} aria-label="Close">
          ✕
        </button>
      </div>

      {/* Highlighted-context panel — shown when Ask AI toolbar button was used */}
      {showHighlightPanel && highlightedInfo && (
        <div className="qd__highlight-panel">
          <span className="qd__highlight-label">Selected text</span>
          <button
            className="qd__highlight-dismiss"
            onClick={() => setShowHighlightPanel(false)}
            aria-label="Remove selected text context"
            title="Remove context"
          >
            ✕
          </button>
          <HighlightPreview text={highlightedInfo} />
        </div>
      )}

      {/* Auto-growing input + submit */}
      <div className="qd__input-row">
        <textarea
          ref={textareaRef}
          className="qd__textarea"
          placeholder="Ask a question, make a comment, flag a mistake"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setExpanded(true);
          }}
          rows={1}
          onPointerDown={() => setExpanded(true)}
          onKeyDown={handleTextareaKeyDown}
          aria-label="Your question or comment"
          aria-autocomplete="list"
        />
        <button
          className="qd__submit"
          disabled={!text.trim()}
          onClick={handleSubmit}
          tabIndex={-1}
          aria-label="Submit"
          title="Submit"
        >
          <SendIcon />
        </button>
      </div>

      {/* Empty state: existing threads */}
      {!query && existing.length > 0 && (
        <ul className="qd__list">
          {existing.map((c) => (
            <li key={c._id}>
              <button
                className="qd__existing-item"
                onClick={() => {
                  openComments(frameId, c._id);
                  if (!window.matchMedia("(min-width: 2225px)").matches)
                    onClose();
                }}
                title={c.title}
              >
                <span
                  className={`qd__type-badge qd__type-badge--${c.type === "COMMENT_QUESTION" ? "q" : "d"}`}
                >
                  {c.type === "COMMENT_QUESTION" ? "Q" : "D"}
                </span>
                <span className="qd__existing-title">{c.title}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Typing state: autocomplete */}
      {query && (
        <ul className="qd__suggestions" ref={listRef} role="listbox">
          {allSuggestions.length === 0 ? (
            <li className="qd__no-results">No matches found</li>
          ) : (
            <>
              {questionMatches.length > 0 && (
                <>
                  <li
                    className="qd__suggestion-group-label"
                    role="presentation"
                  >
                    Discussions
                  </li>
                  {questionMatches.map((c, i) => (
                    <li key={c._id} role="option">
                      <button
                        className="qd__suggestion-item"
                        data-idx={i}
                        onClick={() =>
                          handleSelect({ id: c._id, kind: "question" })
                        }
                        onKeyDown={(e) => handleItemKeyDown(e, i)}
                      >
                        <span
                          className={`qd__type-badge qd__type-badge--${c.type === "COMMENT_QUESTION" ? "q" : "d"}`}
                        >
                          {c.type === "COMMENT_QUESTION" ? "Q" : "D"}
                        </span>
                        <span className="qd__suggestion-label">{c.title}</span>
                      </button>
                    </li>
                  ))}
                </>
              )}
              {supportMatches.length > 0 && (
                <>
                  <li
                    className="qd__suggestion-group-label"
                    role="presentation"
                  >
                    Support topics
                  </li>
                  {supportMatches.map((s, i) => {
                    const globalIdx = questionMatches.length + i;
                    return (
                      <li key={s.id} role="option">
                        <button
                          className="qd__suggestion-item"
                          data-idx={globalIdx}
                          onClick={() =>
                            handleSelect({ id: s.id, kind: "support" })
                          }
                          onKeyDown={(e) => handleItemKeyDown(e, globalIdx)}
                        >
                          <span className="qd__type-badge qd__type-badge--s">
                            S
                          </span>
                          <span className="qd__suggestion-label">
                            {s.label}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </>
              )}
            </>
          )}
        </ul>
      )}
    </div>
  );
}
